import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  ArrowRight, Building2, Handshake, Mail, Phone, User,
} from "lucide-react";
import { CguModal } from "@/features/sponsorship/components/CreateRecommendationForm/CguModal";
import { IconControlledInput } from "@/features/sponsorship/components/CreateRecommendationForm/IconControlledInput";
import { SectionDivider } from "@/features/sponsorship/components/CreateRecommendationForm/SectionDivider";
import { useCreateRecommendation } from "@/features/sponsorship/hooks/useCreateRecommendation";
import {
  createRecommendationSchema,
  type CreateRecommendationFormData,
} from "@/features/sponsorship/schemas/createRecommendation.schema";
import { ButtonWithLoader } from "@/shared/components/customButtons/ButtonWithSpinner";
import { P } from "@/shared/ui/typographies/paragraph";
import { cn } from "@/shared/utils/style.util";

type CreateRecommendationFormProps = {
  remainingRecommendations: number
  onSuccess?: () => void
};

export const CreateRecommendationForm = ({ remainingRecommendations, onSuccess }: CreateRecommendationFormProps) => {
  const isFormDisabled = remainingRecommendations === 0;
  const [isCguAccepted, setIsCguAccepted] = useState(false);
  const [isCguModalOpen, setIsCguModalOpen] = useState(false);

  const {
    createRecommendation, isPending, serverErrors, clearServerErrors,
  } = useCreateRecommendation();

  const {
    control, handleSubmit, reset, setError,
  } = useForm<CreateRecommendationFormData>({
    resolver: zodResolver(createRecommendationSchema),
    defaultValues: {
      first_name: "",
      last_name: "",
      email: "",
      phone: "",
      cse: "",
    },
  });

  useEffect(() => {
    if (!serverErrors?.details) return;

    const fieldKeys = ["first_name", "last_name", "email", "phone", "cse"] as const;

    for (const field of fieldKeys) {
      const fieldErrors = serverErrors.details[field];
      if (fieldErrors?.length) {
        setError(field, { message: fieldErrors[0].error });
      }
    }
  }, [serverErrors, setError]);

  const onSubmit = async (formData: CreateRecommendationFormData) => {
    clearServerErrors();
    try {
      await createRecommendation(formData);
      reset();
      onSuccess?.();
    }
    catch {
      // Errors handled in the hook
    }
  };

  return (
    <div
      className={cn(isFormDisabled && "pointer-events-none opacity-55")}
      aria-disabled={isFormDisabled}
      data-testid="recommendation-form-container"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5">
        {/* Zone 2 — Contact référent */}
        <SectionDivider icon={User} label="Contact référent" />
        <div className="grid grid-cols-1 gap-3.5 md:grid-cols-2">
          <IconControlledInput
            id="first_name"
            label="Prénom"
            name="first_name"
            icon={User}
            placeholder="Jean"
            control={control}
            required
            disabled={isPending}
          />
          <IconControlledInput
            id="last_name"
            label="Nom"
            name="last_name"
            icon={User}
            placeholder="Dupont"
            control={control}
            required
            disabled={isPending}
          />
        </div>
        <div className="grid grid-cols-1 gap-3.5 md:grid-cols-2">
          <IconControlledInput
            id="email"
            label="Email"
            name="email"
            icon={Mail}
            type="email"
            placeholder="jean@exemple.com"
            control={control}
            required
            disabled={isPending}
          />
          <IconControlledInput
            id="phone"
            label="Téléphone"
            name="phone"
            icon={Phone}
            type="tel"
            placeholder="06 12 34 56 78"
            control={control}
            required
            disabled={isPending}
          />
        </div>

        {/* Zone 3 — Entreprise */}
        <SectionDivider icon={Building2} label="Entreprise" />
        <IconControlledInput
          id="cse"
          label="Nom du CSE"
          name="cse"
          icon={Building2}
          placeholder="Nom de l'entreprise / CSE"
          control={control}
          required
          disabled={isPending}
        />

        {/* Zone 4 — CGU + Footer */}
        <div className="flex items-start gap-2.5">
          <input
            type="checkbox"
            id="cgu-checkbox"
            checked={isCguAccepted}
            onChange={(event) => setIsCguAccepted(event.target.checked)}
            aria-required="true"
            aria-describedby="cgu-description"
            className="h-4 w-4 shrink-0 rounded-md border-gray-300 text-primary accent-primary cursor:pointer dark:border-gray-600 dark:bg-gray-700 dark:focus:ring-offset-gray-800"
          />
          <label htmlFor="cgu-checkbox" id="cgu-description" className="text-xs leading-snug text-gray-600 dark:text-gray-400">
            En cochant cette case, vous acceptez les
            {" "}
            <button
              type="button"
              onClick={() => setIsCguModalOpen(true)}
              className="font-medium text-primary underline underline-offset-2 hover:text-primary-emphasis dark:text-primary dark:hover:text-primary-emphasis"
            >
              conditions générales d'utilisation
            </button>
            .
          </label>
        </div>

        <div className="flex items-center justify-between border-gray-200 dark:border-gray-700">
          <ButtonWithLoader
            type="submit"
            color="primary"
            isLoading={isPending}
            disabled={isPending || isFormDisabled || !isCguAccepted}
          >
            Recommander ce contact
            <ArrowRight size={15} className="ml-1.5" />
          </ButtonWithLoader>
          <div className="flex items-center gap-1.5">
            <Handshake size={13} className="text-gray-400 dark:text-gray-500" aria-hidden />
            <P size="xs" color="muted">
              Merci pour votre confiance
            </P>
          </div>
        </div>
      </form>

      <CguModal isOpen={isCguModalOpen} onClose={() => setIsCguModalOpen(false)} />
    </div>
  );
};

CreateRecommendationForm.displayName = "CreateRecommendationForm";
